/**
 * Ask Agent Question Generation Helper
 * =====================================
 * Maps metrics to contextual questions and best-fit agents
 * Enables "Ask Agent" buttons on any data point
 */

// Retailer to Agent ID mappings
const RETAILER_AGENT_MAP: Record<string, string> = {
  'ulta': 'ulta_merchandising',
  '10000000-0000-0000-0000-000000000001': 'ulta_merchandising',
  'macys': 'macys_performance',
  '10000000-0000-0000-0000-000000000003': 'macys_performance',
  'jcp': 'jcp_performance',
  '10000000-0000-0000-0000-000000000004': 'jcp_performance'
};

// Metric to best agent mappings
const METRIC_AGENT_MAP: Record<string, string> = {
  // Revenue metrics -> merchandising or retailer-specific
  'revenue': 'auto', // Will use retailer-specific
  'sales': 'auto',
  'totalRevenue': 'auto',
  
  // Inventory metrics -> inventory agent
  'inventory': 'inventory_risk_replenishment',
  'stockout': 'inventory_risk_replenishment',
  'wos': 'inventory_risk_replenishment',
  'weeksOfSupply': 'inventory_risk_replenishment',
  'coverage': 'inventory_risk_replenishment',
  
  // Forecast metrics -> forecast agent
  'forecast': 'forecast_explainer',
  'prediction': 'forecast_explainer',
  'mape': 'forecast_explainer',
  
  // Promo metrics -> promo agent
  'promo': 'promo_pricing',
  'promotion': 'promo_pricing',
  'discount': 'promo_pricing',
  'margin': 'promo_pricing',
  'price': 'promo_pricing'
};

// Helper to format delta as percentage (delta is stored as decimal, e.g., 0.767 = 76.7%)
const formatDeltaPercent = (delta: number): string => {
  return (Math.abs(delta) * 100).toFixed(1);
};

// Metric to question templates
const METRIC_QUESTIONS: Record<string, (context: MetricContext) => string> = {
  revenue: (ctx) => 
    ctx.delta && ctx.delta !== 0
      ? `Why did revenue ${ctx.delta > 0 ? 'increase' : 'decrease'} ${formatDeltaPercent(ctx.delta)}% ${ctx.week ? `in week ${ctx.week}` : 'this week'}?`
      : `What's driving revenue performance ${ctx.week ? `in week ${ctx.week}` : 'this week'}?`,
  
  sales: (ctx) =>
    `What's driving sales performance ${ctx.week ? `in week ${ctx.week}` : 'this week'}?`,
  
  totalRevenue: (ctx) =>
    ctx.delta && ctx.delta !== 0
      ? `Why did total revenue ${ctx.delta > 0 ? 'increase' : 'decrease'} ${formatDeltaPercent(ctx.delta)}%?`
      : `Explain the total revenue performance`,
  
  units: (ctx) =>
    ctx.delta && ctx.delta !== 0
      ? `Why did units ${ctx.delta > 0 ? 'increase' : 'decrease'} ${formatDeltaPercent(ctx.delta)}%?`
      : `What's driving unit performance?`,
  
  inventory: (ctx) =>
    `What are the inventory risks right now? Any stockouts I should know about?`,
  
  stockout: (ctx) =>
    ctx.value 
      ? `Why is ${ctx.value}% of SKUs out of stock? What's the revenue impact?`
      : `What's causing stockouts and how much revenue are we losing?`,
  
  wos: (ctx) =>
    ctx.value
      ? `Why is weeks of supply at ${ctx.value}? Is this optimal?`
      : `Analyze our weeks of supply and identify any risks`,
  
  weeksOfSupply: (ctx) =>
    `What SKUs have critically low weeks of supply?`,
  
  coverage: (ctx) =>
    `Which SKUs have low inventory coverage? What's the stockout risk?`,
  
  forecast: (ctx) =>
    `How accurate was our forecast? What SKUs missed significantly?`,
  
  prediction: (ctx) =>
    `Explain the forecast accuracy for this period`,
  
  mape: (ctx) =>
    ctx.value
      ? `Why is forecast MAPE at ${ctx.value}%? What's causing the error?`
      : `Which SKUs have the highest forecast error and why?`,
  
  promo: (ctx) =>
    `How did our promotions perform? What was the true incremental lift?`,
  
  promotion: (ctx) =>
    `What's the ROI on current promotions? Any underperformers?`,
  
  discount: (ctx) =>
    ctx.value
      ? `Is ${ctx.value}% discount optimal? What's the margin impact?`
      : `How are discounts affecting our margin?`,
  
  margin: (ctx) =>
    ctx.delta && ctx.delta !== 0
      ? `Why did margin ${ctx.delta > 0 ? 'improve' : 'decline'} ${formatDeltaPercent(ctx.delta)} points?`
      : `What's impacting our margin this period?`,
  
  price: (ctx) =>
    `What pricing opportunities exist? Which SKUs can we optimize?`,
  
  // Generic fallback
  default: (ctx) =>
    `Tell me about ${ctx.metric} performance`
};

export interface MetricContext {
  metric: string;
  value?: number | string;
  delta?: number; // Percentage change
  week?: string;
  sku?: string;
  retailer?: string;
  retailerId?: string;
}

export interface AskAgentParams {
  question: string;
  agentId: string;
}

/**
 * Generate a contextual question and select the best agent for a metric
 */
export function getQuestionForMetric(
  metric: string,
  context: Partial<MetricContext> = {}
): AskAgentParams {
  // Build full context
  const fullContext: MetricContext = {
    metric,
    ...context
  };
  
  // Determine best agent
  let agentId = METRIC_AGENT_MAP[metric.toLowerCase()] || 'executive_summary';
  
  // If 'auto', use retailer-specific agent
  if (agentId === 'auto') {
    const retailerKey = context.retailerId || context.retailer || '';
    agentId = RETAILER_AGENT_MAP[retailerKey] || 'executive_summary';
  }
  
  // Generate question
  const questionGenerator = METRIC_QUESTIONS[metric.toLowerCase()] || METRIC_QUESTIONS.default;
  const question = questionGenerator(fullContext);
  
  return { question, agentId };
}

/**
 * Build the Agent Hub URL with pre-filled question
 */
export function buildAgentHubUrl(params: AskAgentParams): string {
  const searchParams = new URLSearchParams({
    q: params.question,
    agent: params.agentId,
    forceAgent: 'true',
    submit: 'true'
  });
  
  return `/agent-hub?${searchParams.toString()}`;
}

/**
 * Quick helper to get agent hub URL for a metric
 */
export function getAgentHubUrlForMetric(
  metric: string,
  context: Partial<MetricContext> = {}
): string {
  const params = getQuestionForMetric(metric, context);
  return buildAgentHubUrl(params);
}

